import {
  Component,
  ViewChild,
  ElementRef,
  Output,
  EventEmitter,
  Input,
  OnInit,
} from "@angular/core";
import { FormGroup, FormBuilder } from "@angular/forms";
import { FileService } from "../../services/common/file.service";
import { ILoggedUserData } from "../../interfaces/common/loggedUserData";
import { ObjectFile } from "../../interfaces/shared/file-upload/upload-data";

@Component({
  selector: "app-file-upload",
  templateUrl: "./file-upload.component.html",
  styleUrls: ["./file-upload.component.css"],
})
export class FileUploadComponent implements OnInit {
  @ViewChild("fileInput") fileInput: ElementRef;

  @Input() acceptedFileTypes: string = "*";
  @Input() maxFileSize: number = 1024; // Default max size in MB
  @Input() maxFileNameLength: number = 15; // Default max length for file names before truncation
  @Input() ObjectId: number;
  @Input() EntityType: string;
  @Input() currentUser: ILoggedUserData;
  @Output() fileSelected = new EventEmitter<File>();
  @Output() uploadSuccess = new EventEmitter<any>();
  @Output() uploadError = new EventEmitter<any>();
  @Output() deleteSuccess = new EventEmitter<any>();

  listOfFiles: ObjectFile[] = [];
  uploadForm: FormGroup;
  selectedFile: File | null = null;
  isHovering: boolean = false;
  errorMessage: string[] = [];
  isUploading: boolean = false;
  uploadSuccessMessage: string = "";
  isDeleting: boolean = false;
  deleteSuccessMessage: string = "";

  constructor(
    private fileService: FileService,
    private formBuilder: FormBuilder
  ) {}

  ngOnInit() {
    // Add 'file' and 'notes' controls to match the HTML
    this.uploadForm = this.formBuilder.group({
      file: [""],
      notes: [""],
    });

    this.loadFiles();
  }

  loadFiles() {
    this.fileService
      .getAllFiles(this.EntityType, this.ObjectId)
      .subscribe((data) => {
        if (data) {
          this.listOfFiles = data;
        }
      });
  }

  triggerFileInput() {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.processFile(input.files[0]);
    }
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isHovering = false;

    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      this.processFile(event.dataTransfer.files[0]);
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isHovering = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isHovering = false;
  }

  private processFile(file: File) {
    this.errorMessage = [];
    this.uploadSuccessMessage = "";
    this.isUploading = true;

    // Check file size
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > this.maxFileSize) {
      this.errorMessage.push(
        `File size exceeds maximum limit of ${this.maxFileSize}MB`
      );
      return;
    }

    // Store file locally and in the form
    this.selectedFile = file;
    this.uploadForm.patchValue({ file });
    this.fileSelected.emit(file);

    // Immediately proceed with upload after selection
    this.onSubmit();
  }

  onSubmit() {
    // Use the control's value instead of selectedFile
    const file = this.uploadForm.get("file")?.value;
    if (!file) {
      this.errorMessage.push("No file selected");
      return;
    }

    // Check if the file type is allowed
    const fileType = file.type;
    const acceptedTypes = this.acceptedFileTypes.split(",");
    const mimeToExtMap: Record<string, string[]> = {
      "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        [".pptx"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "application/pdf": [".pdf"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
    };

    const fileName = file.name;
    const fileExt = fileName.substring(fileName.lastIndexOf(".")).toLowerCase();

    const isValidType = acceptedTypes.some((type) => {
      const trimmedType = type.trim();
      if (trimmedType === "*" || trimmedType === fileType) {
        return true;
      }
      if (mimeToExtMap[fileType] && mimeToExtMap[fileType].includes(fileExt)) {
        return true;
      }
      if (fileType.includes("/") && trimmedType === fileType.split("/")[1]) {
        return true;
      }
      if (
        trimmedType.startsWith(".") &&
        trimmedType.toLowerCase() === fileExt
      ) {
        return true;
      }
      return false;
    });

    if (!isValidType) {
      this.errorMessage.push("File type not allowed");
      return;
    }

    this.isUploading = true;
    this.errorMessage = [];
    this.uploadSuccessMessage = "";

    // Create FormData object
    const formData = new FormData();
    formData.append("file", file);
    formData.append("uploadDate", new Date().toISOString());
    formData.append("entityType", this.EntityType);
    formData.append("facilityName", this.currentUser?.facilityName || "");
    if (this.ObjectId) {
      formData.append("objectId", this.ObjectId.toString());
    }

    this.fileService.uploadFile(formData).subscribe({
      next: (response) => {
        this.isUploading = false;
        this.uploadSuccessMessage = `File uploaded successfully`;
        this.uploadSuccess.emit(response);

        // Refresh the file list after successful upload
        this.loadFiles();

        // Clear the success message after 5 seconds
        setTimeout(() => {
          this.uploadSuccessMessage = "";
        }, 5000);
      },
      error: (error) => {
        this.isUploading = false;
        this.errorMessage.push("Error uploading file");
        this.uploadError.emit(error);
      },
    });
  }

  clearSelectedFile() {
    this.selectedFile = null;
    this.uploadForm.get("file")?.setValue("");
    this.errorMessage = [];
    this.uploadSuccessMessage = "";
  }

  // Optionally used in the HTML to display the selected file's name
  getFileName() {
    return this.selectedFile ? this.selectedFile.name : "";
  }

  // Method to truncate file names for display
  truncateFileName(fileName: string): string {
    if (!fileName) return "";

    // Get the file extension
    const lastDotIndex = fileName.lastIndexOf(".");
    if (lastDotIndex === -1) {
      // No extension, just truncate if needed
      return fileName.length > this.maxFileNameLength
        ? fileName.substring(0, this.maxFileNameLength) + "..."
        : fileName;
    }

    const name = fileName.substring(0, lastDotIndex);
    const extension = fileName.substring(lastDotIndex);

    // Truncate the name part if it's longer than maxFileNameLength characters
    return name.length > this.maxFileNameLength
      ? name.substring(0, this.maxFileNameLength) + "..." + extension
      : fileName;
  }

  // Format file size to display in appropriate units (KB, MB, GB)
  formatFileSize(bytes: number): string {
    if (!bytes || bytes === 0) return "0 KB";

    const KB = 1024;
    const MB = KB * 1024;
    const GB = MB * 1024;

    if (bytes >= GB) {
      return (bytes / GB).toFixed(2) + " GB";
    } else if (bytes >= MB) {
      return (bytes / MB).toFixed(2) + " MB";
    } else {
      return (bytes / KB).toFixed(1) + " KB";
    }
  }

  downloadFile(file: ObjectFile) {
    this.fileService
      .downloadFile(
        file.fileId,
        this.EntityType,
        this.currentUser?.facilityName || ""
      )
      .subscribe({
        next: (blob: Blob) => {
          // Create a URL for the blob
          const url = window.URL.createObjectURL(blob);

          // Create a temporary anchor element
          const a = document.createElement("a");
          a.href = url;
          a.download = file.fileName;

          // Append to the document, click it, and remove it
          document.body.appendChild(a);
          a.click();

          // Clean up
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        },
        error: (error) => {
          console.error("Error downloading file:", error);
          this.errorMessage.push(
            "Error downloading file. Please try again later."
          );
        },
      });
  }

  confirmDeleteFile(file: ObjectFile) {
    if (confirm(`Are you sure you want to delete ${file.fileName}?`)) {
      this.deleteFile(file);
    }
  }

  deleteFile(file: ObjectFile) {
    this.isDeleting = true;
    this.errorMessage = [];
    this.deleteSuccessMessage = "";

    this.fileService
      .deleteFile(
        file.fileId,
        this.EntityType,
        this.currentUser?.facilityName || ""
      )
      .subscribe({
        next: (response) => {
          this.isDeleting = false;
          this.deleteSuccessMessage = `File deleted successfully`;
          this.deleteSuccess.emit(response);

          // Refresh the file list after successful deletion
          this.loadFiles();

          // Update the success message
          this.uploadSuccessMessage = `File deleted successfully`;

          // Clear the success message after 5 seconds
          setTimeout(() => {
            this.uploadSuccessMessage = "";
          }, 5000);
        },
        error: (error) => {
          this.isDeleting = false;
          this.errorMessage.push("Error deleting file");
          this.uploadError.emit(error);
        },
      });
  }
}
